class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement = document.getElementById('shopping-cart-line-item-status');
    this.quantityUpdateTimeout = null;
    this.#updateChain = Promise.resolve();
    /** @type {Map<string, { line: string, quantity: number, key: string }>} */
    this.#pendingByKey = new Map();

    this.addEventListener('change', this.onChange.bind(this));
  }

  /** @type {Promise<unknown>} */
  #updateChain;

  /** @type {Map<string, { line: string, quantity: number, key: string }>} */
  #pendingByKey;

  disconnectedCallback() {
    clearTimeout(this.quantityUpdateTimeout);
    this.quantityUpdateTimeout = null;
  }

  onChange(event) {
    const input = event.target;

    if (!(input instanceof HTMLInputElement) || !input.classList.contains('quantity__input')) {
      return;
    }

    const quantity = input.dataset.pendingQuantity ?? input.value;
    delete input.dataset.pendingQuantity;
    const parsedQuantity = parseInt(quantity, 10);

    if (Number.isNaN(parsedQuantity)) return;

    clearTimeout(this.quantityUpdateTimeout);

    const line = input.dataset.index;
    const key = input.dataset.key;

    if (!key) return;

    this.#pendingByKey.set(key, { line, quantity: parsedQuantity, key });

    const performUpdate = () => {
      const pending = this.#pendingByKey.get(key);
      this.#pendingByKey.delete(key);

      if (!pending) return;

      this.updateQuantity(pending.line, pending.quantity, pending.key);
    };

    if (parsedQuantity === 0) {
      performUpdate();
      return;
    }

    this.quantityUpdateTimeout = setTimeout(performUpdate, 300);
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        section: 'cart-drawer',
        selector: '#CartDrawer-Content',
      },
      {
        id: 'main-cart',
        section: 'main-cart',
        selector: '#main-cart-items',
      },
      {
        id: 'cart-icon-bubble',
        section: 'header',
        selector: '#cart-icon-bubble',
      },
    ];
  }

  #enqueueUpdate(task) {
    const run = this.#updateChain.then(task, task);
    this.#updateChain = run.catch(() => {});
    return run;
  }

  updateQuantity(line, quantity, key) {
    this.#enqueueUpdate(() => this.#performQuantityUpdate(line, quantity, key));
  }

  #findCartItemByKey(key) {
    for (const item of document.querySelectorAll('.cart-item[data-key]')) {
      if (item.dataset.key === key) {
        return item;
      }
    }

    return null;
  }

  /**
   * When the changed line is a personalised / photo-stitch main product, also update its fee line(s).
   */
  #buildBundleUpdates(key, quantity) {
    const mainItem = this.#findCartItemByKey(key);
    const uniqueId = mainItem?.dataset.uniqueId;

    if (!uniqueId) {
      return null;
    }

    const updates = { [key]: quantity };
    let hasFee = false;

    for (const feeItem of document.querySelectorAll('.cart-item[data-related-main-id]')) {
      if (feeItem.dataset.relatedMainId !== uniqueId) continue;

      const feeKey = feeItem.dataset.key;

      if (!feeKey) continue;

      const perUnit = Math.max(1, Number.parseInt(feeItem.dataset.feePerUnit || '1', 10) || 1);
      updates[feeKey] = quantity <= 0 ? 0 : quantity * perUnit;
      hasFee = true;
    }

    return hasFee ? updates : null;
  }

  #sectionPayload() {
    return {
      sections: this.getSectionsToRender()
        .map((section) => section.section)
        .join(','),
      sections_url: window.location.pathname,
    };
  }

  #postJson(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    }).then(async (response) => {
      if (!response.ok) {
        throw new Error(`Network response was not ok: ${response.status}`);
      }

      return response.json();
    });
  }

  #disableQuantityControls(line) {
    const quantityInput = document.getElementById(`Quantity-${line}`);
    const buttons = [];

    if (!quantityInput) {
      return { quantityInput: null, buttons, previousValue: null };
    }

    const previousValue = quantityInput.getAttribute('value') ?? quantityInput.value;

    quantityInput.setAttribute('disabled', true);
    const wrapper = quantityInput.closest('quantity-input');

    if (wrapper) {
      for (const btn of wrapper.querySelectorAll('button')) {
        btn.setAttribute('disabled', true);
        buttons.push(btn);
      }
    }

    return { quantityInput, buttons, previousValue };
  }

  #enableQuantityControls(quantityInput, buttons) {
    if (quantityInput?.isConnected) {
      quantityInput.removeAttribute('disabled');
    }

    for (const btn of buttons) {
      if (btn.isConnected) {
        btn.removeAttribute('disabled');
      }
    }
  }

  #revertQuantityControls(quantityInput, buttons, previousValue) {
    if (quantityInput?.isConnected && previousValue != null) {
      quantityInput.value = previousValue;
      quantityInput.closest('quantity-input')?.validateButtons?.();
    }

    this.#enableQuantityControls(quantityInput, buttons);
  }

  #performQuantityUpdate(line, quantity, key) {
    const { quantityInput, buttons, previousValue } = this.#disableQuantityControls(line);
    const bundleUpdates = this.#buildBundleUpdates(key, quantity);
    const sections = this.#sectionPayload();

    const request = bundleUpdates
      ? this.#postJson(`${window.routes.cart_update_url}`, { updates: bundleUpdates, ...sections })
      : this.#postJson(`${window.routes.cart_change_url}`, { id: key, quantity, ...sections });

    return request
      .then((parsedState) => {
        if (parsedState.errors) {
          this.#revertQuantityControls(quantityInput, buttons, previousValue);
          return;
        }

        this.renderSections(parsedState.sections);
        this.updateLiveRegions(line, parsedState.item_count);
      })
      .catch((error) => {
        this.#revertQuantityControls(quantityInput, buttons, previousValue);
        console.error('Cart Error:', error);
      });
  }

  renderSections(sectionsState) {
    if (!sectionsState) return;

    for (const section of this.getSectionsToRender()) {
      const html = sectionsState[section.section];
      if (!html) continue;

      const dom = new DOMParser().parseFromString(html, 'text/html');
      const replacement = dom.querySelector(section.selector) || dom.querySelector(`#${section.id}`);
      const elementToReplace = document.querySelector(section.selector) || document.getElementById(section.id);

      if (elementToReplace && replacement) {
        elementToReplace.replaceWith(replacement);

        if (section.id === 'cart-icon-bubble') {
          syncDockCartCountBubble();
        }
      }
    }
  }

  updateLiveRegions(line, itemCount) {
    if (this.lineItemStatusElement) {
      this.lineItemStatusElement.setAttribute('aria-hidden', true);
      this.lineItemStatusElement.setAttribute('aria-hidden', false);

      setTimeout(() => {
        this.lineItemStatusElement.setAttribute('aria-hidden', true);
      }, 1000);
    }
  }
}

if (!customElements.get('cart-items')) {
  customElements.define('cart-items', CartItems);
}
